import { forwardRef } from "react"
import useTranslation from "../../hooks/useTranslation"
import { FormGroup } from "../form/FormComponents"
import FormElem from "./FormElem"
import ModalFormElem from "./ModalFormElem"
import { conditionFields, resolveVisibility } from "../../../../common/model/condition/evaluate"
import { resolveElement, conditionalFieldsOf } from "../../../../common/model/condition/resolveElement"
import useConditionValues from "./useConditionValues"

const ElemToShow = forwardRef(({ elem, lineId, key, validated, section, entity, useStore, path, options, lang }, ref) => {

    // This wrapper is the only place a hidden field can still hold its space:
    // FormCol is `flex: 1 0 0%`, so dropping it lets the siblings expand.
    // 'reserve' keeps the cell; FormElem then renders nothing inside it.
    const conditionKeys = conditionFields(elem?.condition)
    const conditionValues = useConditionValues(useStore, conditionKeys, lineId, path, elem?.subObject)
    const visibility = resolveVisibility(elem, {
        values: conditionValues,
        context: { mode: options?.mode },
    })

    return (
        visibility !== 'collapse' && <FormGroup.Col key={key} $weight={elem.weight}>
            {elem.type === 'modal'
                ? <ModalFormElem item={elem} key={key}
                    validated={validated} section={section} lineId={lineId} entity={entity} useStore={useStore} path={path} options={options}
                  />
                : <FormElem item={elem} key={key}
                    validated={validated} section={section} lineId={lineId} entity={entity} useStore={useStore} path={path} options={options}
                    ref={(elem.focus) ? ref : null}
                  />
            }
        </FormGroup.Col>
    );
}
)


const GroupFormElem = forwardRef(({ section, lang, item, lineId, validated, entity, useStore, path, options }, ref) => {
    const { t } = useTranslation();

    // Same resolver as every other element, which is how a group finally honours
    // `$mode` — it silently ignored `condition: { mode }` before.
    // `conditionalFieldsOf` joins the subscription so the group can also restyle
    // itself (`direction`, `mobileRow`, `width`) per state. Only `item` is
    // resolved here: each field resolves its own `conditionals` inside FormElem
    // or ModalFormElem, and resolving them here instead would hand those
    // components a base whose hook-gating properties change with form state.
    const groupKeys = [...new Set([
        ...conditionFields(item?.condition),
        ...conditionalFieldsOf(item),
    ])]
    const groupValues = useConditionValues(useStore, groupKeys, lineId, path, item?.subObject)
    const sources = { values: groupValues, context: { mode: options?.mode } }

    item = resolveElement(item, sources)
    if (resolveVisibility(item, sources) !== 'show') return null

    return (
        <FormGroup name='FormGroup' key={item.name} $margin='0'>
            <FormGroup.Row $direction={item.direction} $mobileRow={item.mobileRow}>
                {item.fields.map((elem, ix) => {
                    return (
                        <ElemToShow elem={elem} key={`group-${ix}`} validated={validated} section={section} entity={entity} useStore={useStore} path={path} options={options} lineId={lineId} ref={ref} lang={lang} />
                    );
                })}
            </FormGroup.Row>
        </FormGroup>
    );
});

export default GroupFormElem;